/* ========================================
   Custom Video Section — Isolated JS
   Custom element: <custom-video-player>
   ======================================== */

if (typeof CustomVideoPlayer !== 'function') {

  class CustomVideoPlayer extends HTMLElement {

    constructor() {
      super();
      this.videoEl = null;
      this.posterEl = null;
      this.overlayEl = null;
      this.playBtn = null;
      this.soundToggle = null;
      this.progressBar = null;
      this.progressFill = null;
      this.pauseIndicator = null;
      this.isPlaying = false;
      this.hasStarted = false;
      this.isExternal = false;
      this._progressRAF = null;
    }

    connectedCallback() {
      this.videoEl = this.querySelector('[data-cvs-video]');
      this.posterEl = this.querySelector('[data-cvs-poster]');
      this.overlayEl = this.querySelector('[data-cvs-overlay]');
      this.playBtn = this.querySelector('[data-cvs-play-btn]');
      this.soundToggle = this.querySelector('[data-cvs-sound-toggle]');
      this.progressBar = this.querySelector('[data-cvs-progress-bar]');
      this.progressFill = this.querySelector('[data-cvs-progress-fill]');
      this.pauseIndicator = this.querySelector('[data-cvs-pause-indicator]');
      this.isExternal = this.dataset.cvsType === 'external';

      this._bindEvents();
      this._handleLazyLoad();
      this._handleAutoplay();
    }

    disconnectedCallback() {
      if (this._progressRAF) {
        cancelAnimationFrame(this._progressRAF);
      }
    }

    _bindEvents() {
      // Play button click
      if (this.playBtn) {
        this.playBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._startPlayback();
        });
      }

      // Click to play/pause on video wrapper
      var wrapper = this.querySelector('[data-cvs-wrapper]');
      if (wrapper && this.dataset.cvsClickToggle === 'true') {
        wrapper.addEventListener('click', (e) => {
          if (e.target.closest('[data-cvs-play-btn]') ||
              e.target.closest('[data-cvs-sound-toggle]') ||
              e.target.closest('[data-cvs-cta-btn]')) {
            return;
          }
          if (this.hasStarted) {
            this._togglePlayPause();
          } else {
            this._startPlayback();
          }
        });
      }

      // Sound toggle
      if (this.soundToggle && this.videoEl) {
        this.soundToggle.addEventListener('click', (e) => {
          e.stopPropagation();
          this._toggleMute();
        });
      }

      // Progress bar tracking
      if (this.videoEl && this.progressFill) {
        this._trackProgress();
      }

      // Video ended
      if (this.videoEl) {
        this.videoEl.addEventListener('ended', () => {
          this.isPlaying = false;
          this._showOverlay();
          this._showPlayBtn();
        });
      }
    }

    _handleAutoplay() {
      if (!this.videoEl || this.isExternal) return;
      if (this.dataset.cvsAutoplay !== 'true') return;

      var self = this;

      function attemptPlay() {
        var playPromise = self.videoEl.play();
        if (playPromise !== undefined) {
          playPromise.then(function() {
            self.isPlaying = true;
            self.hasStarted = true;
            self._hidePoster();
            self._hideOverlay();
            self._hidePlayBtn();
          }).catch(function() {
            // Autoplay blocked by browser — show play button
            self._showPlayBtn();
            self._showOverlay();
          });
        }
      }

      // If video already has enough data, play now
      if (this.videoEl.readyState >= 3) {
        attemptPlay();
      } else {
        // Wait until the browser has loaded enough to play
        this.videoEl.addEventListener('canplay', attemptPlay, { once: true });
      }
    }

    _handleLazyLoad() {
      if (!this.videoEl) return;

      var src = this.videoEl.dataset.src;
      if (!src) return; // Not a lazy-loaded video

      var self = this;
      var isAutoplay = this.dataset.cvsAutoplay === 'true';

      // If autoplay is on, load the source immediately — don't wait for scroll
      if (isAutoplay) {
        this.videoEl.src = src;
        this.videoEl.load();
        return;
      }

      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting) {
              self.videoEl.src = src;
              self.videoEl.load();
              observer.unobserve(self);
            }
          });
        }, { rootMargin: '200px' });
        observer.observe(this);
      } else {
        this.videoEl.src = src;
        this.videoEl.load();
      }
    }

    _startPlayback() {
      if (this.isExternal) {
        this._startExternalPlayback();
        return;
      }

      if (!this.videoEl) return;

      var playPromise = this.videoEl.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          this.isPlaying = true;
          this.hasStarted = true;
          this._hidePoster();
          this._hideOverlay();
          this._hidePlayBtn();
        }).catch(function() {});
      }
    }

    _startExternalPlayback() {
      var template = this.querySelector('[data-cvs-iframe-template]');
      var iframeWrapper = this.querySelector('[data-cvs-iframe-wrapper]');

      if (template && iframeWrapper) {
        iframeWrapper.innerHTML = '';
        iframeWrapper.appendChild(template.content.firstElementChild.cloneNode(true));
        this.hasStarted = true;
        this._hidePoster();
        this._hideOverlay();
        this._hidePlayBtn();
      }
    }

    _togglePlayPause() {
      if (this.isExternal || !this.videoEl) return;

      if (this.isPlaying) {
        this.videoEl.pause();
        this.isPlaying = false;
        this._showPauseIndicator(true);
      } else {
        var playPromise = this.videoEl.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            this.isPlaying = true;
            this._showPauseIndicator(false);
          }).catch(function() {});
        }
      }
    }

    _toggleMute() {
      if (!this.videoEl) return;
      this.videoEl.muted = !this.videoEl.muted;
      this._updateSoundIcon();
    }

    _updateSoundIcon() {
      if (!this.soundToggle || !this.videoEl) return;
      var muteIcon = this.soundToggle.querySelector('[data-cvs-icon-muted]');
      var soundIcon = this.soundToggle.querySelector('[data-cvs-icon-sound]');
      if (muteIcon && soundIcon) {
        muteIcon.style.display = this.videoEl.muted ? 'block' : 'none';
        soundIcon.style.display = this.videoEl.muted ? 'none' : 'block';
      }
    }

    _trackProgress() {
      var self = this;
      function update() {
        if (self.videoEl && self.progressFill && self.videoEl.duration) {
          var pct = (self.videoEl.currentTime / self.videoEl.duration) * 100;
          self.progressFill.style.width = pct + '%';
        }
        self._progressRAF = requestAnimationFrame(update);
      }
      update();
    }

    _hidePoster() {
      if (this.posterEl) {
        this.posterEl.classList.add('cvs-poster--hidden');
      }
    }

    _showOverlay() {
      if (this.overlayEl) {
        this.overlayEl.classList.remove('cvs-overlay--hidden');
      }
    }

    _hideOverlay() {
      if (this.overlayEl) {
        this.overlayEl.classList.add('cvs-overlay--hidden');
      }
    }

    _showPlayBtn() {
      if (this.playBtn) {
        this.playBtn.classList.remove('cvs-play-btn--hidden');
      }
    }

    _hidePlayBtn() {
      if (this.playBtn) {
        this.playBtn.classList.add('cvs-play-btn--hidden');
      }
    }

    _showPauseIndicator(isPaused) {
      if (!this.pauseIndicator) return;
      var indicator = this.pauseIndicator;
      var playIcon = indicator.querySelector('[data-cvs-icon-play]');
      var pauseIcon = indicator.querySelector('[data-cvs-icon-pause]');

      if (playIcon && pauseIcon) {
        playIcon.style.display = isPaused ? 'none' : 'block';
        pauseIcon.style.display = isPaused ? 'block' : 'none';
      }

      indicator.classList.remove('cvs-pause-indicator--fade');
      indicator.classList.add('cvs-pause-indicator--show');

      setTimeout(function() {
        indicator.classList.add('cvs-pause-indicator--fade');
        indicator.classList.remove('cvs-pause-indicator--show');
      }, 600);
    }

  }

  if (typeof customElements.get('custom-video-player') === 'undefined') {
    customElements.define('custom-video-player', CustomVideoPlayer);
  }

}
